import { useMemo } from 'react'

import { get } from 'react-hook-form'
import { capitalize } from '@material-ui/core/utils'

import { ADDONS } from 'common/utils/conciergeProduct.constants'
import countries from 'common/utils/data/countries'
import states from 'common/utils/data/states'
import { UK_COUNTY_MAP } from 'common/utils/data/states/uk'
import request from 'common/utils/request'
import { BOX_PRODUCT_TYPE, PAYMENT_KINDS } from 'artkive/stores/product.constants'
import { USA_COUNTRY_KEY } from 'artkive/utils/addressValidationService'
import * as track from 'artkive/utils/tracker'

export const normalizePhoneNumber = (value) => value?.replace(/\D+/g, '')

export const normalizeCountryCode = (code) => code?.toUpperCase()

export const normalizeStateCode = (country, stateInput) => {
  if (!stateInput || !country) return stateInput

  // GB-specific: map PayPal/ApplePay county names to zone codes
  if (country === 'GB') {
    const mapped = UK_COUNTY_MAP[stateInput.toLowerCase()]
    if (mapped) return mapped
  }

  const countryStates = states[country]
  if (!countryStates) return stateInput

  if (countryStates.some(({ value }) => value === stateInput)) return stateInput

  const match = countryStates.find(({ label }) => label.toLowerCase() === stateInput.toLowerCase())
  return match ? match.value : stateInput
}

const getCountryLabel = (code) => countries.find((c) => c.value === code)?.label || code

export const unsupportedCountryError = (allowedCountries) => {
  if (allowedCountries?.length) {
    const names = allowedCountries.map(getCountryLabel).join(', ')
    return `Order can only be shipped to addresses in ${names}.`
  }
  return 'Order can not be shipped to the selected address.'
}

export const isCountryAllowed = (country, allowedCountries) => {
  if (!allowedCountries || allowedCountries.length === 0) return true
  return allowedCountries.includes(country)
}

export const NOOP = () => null

export const orderBox = async (payload) => {
  try {
    const response = await request.post(Routing.api_v2_orders_box_index(), { ...payload })

    return { response }
  } catch (error) {
    return { error }
  }
}

const addressTraits = (shippingInformation, normalize) => {
  if (!shippingInformation.shipping_zip) return undefined

  const traits = {
    city: shippingInformation.shipping_city,
    country: shippingInformation.shipping_country || USA_COUNTRY_KEY,
    postalCode: shippingInformation.shipping_zip,
    state: shippingInformation.shipping_state,
  }
  if (normalize) {
    traits.city = traits.city?.replace(/\s/g, '')?.toLowerCase()
    traits.country = traits.country.toLowerCase()
    traits.state = traits.state?.toLowerCase()
  }
  return traits
}

// NOTE: normalization is required for FB conversion
const userTraits = (shippingInformation, normalize = true) => {
  const nameParts = (shippingInformation.name || '').split(' ')
  const { 0: firstName, [nameParts.length - 1]: lastName } = nameParts
  const traits = {
    phone: shippingInformation.phone_number,
    email: shippingInformation.email,
    name: shippingInformation.name,
    firstName: capitalize(firstName),
    lastName: capitalize(nameParts.length > 1 ? lastName : ''),
  }
  if (shippingInformation.shipping_zip) {
    traits.address = addressTraits(shippingInformation, normalize)
  }

  return traits
}

export const buildCheckoutUtmParameters = (tracking) => ({
  utm_source: tracking.utmSource,
  utm_campaign: tracking.utmCampaign,
  utm_medium: tracking.utmMedium,
  utm_content: tracking.utmContent,
  utm_term: tracking.utmTerm,
})

export const buildPaymentMethod = (payment, freeOrder) => {
  if (Object.keys(payment || {}).length === 0 || freeOrder) return { method: PAYMENT_KINDS.CREDIT_CARD }

  const expArray = payment.cardExp.split('/')

  return {
    method: PAYMENT_KINDS.CREDIT_CARD,
    name_on_card: payment.name,
    credit_card_number: payment.cardNumber.replace(/\s+/g, ''),
    exp_month: expArray[0],
    exp_year: `20${expArray[1]}`,
    security_code: payment.cardCvc,
  }
}

export const mapStateToAddress = (state) => ({
  phone_number: normalizePhoneNumber(state.phone),
  shipping_address: state.address,
  shipping_address_details: state.address2,
  shipping_city: state.city,
  shipping_country: state.country,
  shipping_state: state.state,
  shipping_zip: state.zipCode,
})

export const mapAppleContactToAddress = (shippingContact) => {
  const shipping_country = normalizeCountryCode(shippingContact.countryCode)
  return {
    phone_number: normalizePhoneNumber(shippingContact.phoneNumber),
    shipping_address: shippingContact.addressLines?.join(' '),
    shipping_address_details: null,
    shipping_country,
    shipping_city: shippingContact.locality,
    shipping_state: normalizeStateCode(shipping_country, shippingContact.administrativeArea),
    shipping_zip: shippingContact.postalCode,
  }
}

export const mapPaypalAddressToAddress = (shippingAddress) => {
  const shipping_country = normalizeCountryCode(shippingAddress.countryCode)
  return {
    shipping_address: shippingAddress.line1,
    shipping_address_details: shippingAddress.line2,
    shipping_city: shippingAddress.city,
    shipping_country,
    shipping_state: normalizeStateCode(shipping_country, shippingAddress.state),
    shipping_zip: shippingAddress.postalCode,
  }
}

export const useProductMeta = (
  productData,
  productLinks,
  { isVipMembershipAvailable = false, defaultCountry = '' } = {},
) =>
  useMemo(() => {
    const addonSections = get(productData, 'properties.options.addon_sections', []).map(({ items, ...rest }) => ({
      ...rest,
      items: items.map((item) => ({
        ...item,
        currency_symbol: productData.currency_symbol,
        currency_code: productData.currency_code,
      })),
    }))
    const checkoutOptions = {
      id: productData.id,
      uuid: productData.uuid,
      type: BOX_PRODUCT_TYPE,
      cover_url: get(productData, 'cover_image.url', ''),
      preview_url: get(productData, 'preview_image.url', ''),
      name: get(productData, 'name', ''),
      back_link: `~${productLinks.landing}`,
      vip_membership: get(productData, 'properties.vip_membership', false),
      processing_time: get(productData, 'properties.processing_time', false),
      shipping_protection: get(productData, 'properties.shipping_protection', false),
      shipping_address: get(productData, 'properties.shipping_address', false),
      taxable: get(productData, 'properties.taxable', false),
      adjust_quantity: get(productData, 'properties.adjust_quantity', false),
      addon_sections: addonSections,
      countries: get(productData, 'properties.countries', []),
      defaultCountry,
      gift: get(productData, 'properties.checkout.gift_section', null),
      source: productData,
    }

    const availableOrderDetails = [
      ...checkoutOptions.addon_sections,
      checkoutOptions.processing_time,
      checkoutOptions.shipping_protection,
      checkoutOptions.gift,
    ].filter(Boolean)

    checkoutOptions.isOrderDetailsAvailable = availableOrderDetails.length > 1
    checkoutOptions.isSomeOrderDetailsAvailable = availableOrderDetails.length > 0
    checkoutOptions.forceExpressCheckoutOnFirstPage = availableOrderDetails.length <= 1

    checkoutOptions.hasSubscriptionAddon =
      (isVipMembershipAvailable && checkoutOptions.vip_membership) ||
      checkoutOptions.addon_sections.some(({ items }) => items.some(({ kind }) => kind === ADDONS.SUBSCRIPTION))

    return checkoutOptions
  }, [productData, isVipMembershipAvailable, productLinks, defaultCountry])

export const trackOrderPurchase = (order, paymentKind, shippingInformation, tracking, purchaseEventId) => {
  const { id, quantity, pricing_object, concierge_product_id, currency_code } = order
  const { promo_discount, promo_discount_details, total } = pricing_object

  track.identifyUser(userTraits(shippingInformation, false))

  /**
   *  NOTE As we don't add taxes right now total doesn't include them at all. Some consumers like
   *  ShareASale requires total without taxes
   */
  track.purchase({
    amount: total,
    discount: promo_discount,
    orderId: id,
    name: shippingInformation.name,
    productId: concierge_product_id,
    product: BOX_PRODUCT_TYPE,
    promo: promo_discount_details.code,
    information: shippingInformation,
    paymentKind,
    tracking,
    context: { traits: userTraits(shippingInformation), messageId: purchaseEventId },
    currency: currency_code,
    quantity,
  })
}

/**
 *
 * @param {object} productData
 * @param {string} kind
 * @returns {object|undefined}
 */
export function getProductAddonByKind(productData, kind) {
  let addOn
  productData.properties.options.addon_sections.find((section) => {
    addOn = section.items.find((addOnItem) => addOnItem.kind === kind)
    return addOn
  })
  return addOn
}
